import React from "react";
import { Link } from "react-router-dom";
import PageShell from "@/components/PageShell";
import { COMPANY } from "@/lib/compliance";

/**
 * Privacy Notice — Phase 2 compliance expansion.
 * Multi-state insurance agency disclosures: information collection,
 * use, sharing recipients, marketing opt-out, document uploads, retention
 * schedule, state privacy rights (CA/CO/CT/VA/UT/TX), children's privacy,
 * security, and updates.
 */
export default function PrivacyPage() {
    return (
        <PageShell
            testId="page-privacy"
            kicker="Compliance"
            title="Privacy Notice"
            seoDescription="How Endurance Casualty Insurance Agency collects, uses, and protects your information. GLBA-aligned consumer privacy notice with state-specific rights."
        >
            <p className="text-sm text-brand-muted">
                <strong className="text-brand-ivory">Effective Date:</strong>{" "}
                June 2026
                <br />
                <strong className="text-brand-ivory">Last Updated:</strong>{" "}
                June 2026
            </p>

            <p>
                {COMPANY.fullLegalName} (&ldquo;{COMPANY.shortName},&rdquo;
                &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;)
                respects your privacy. This Privacy Notice explains what
                personal information we collect when you use{" "}
                <a href={COMPANY.website} rel="noopener noreferrer">
                    {COMPANY.website}
                </a>
                {" "}(the &ldquo;Site&rdquo;), how we use it, with whom we
                share it, how long we keep it, and the choices and rights
                you have.
            </p>

            <h2>1. Information We Collect</h2>
            <p>
                We collect the following categories of information when you
                use the Site, submit our inquiry form, contact us, or upload
                documents:
            </p>
            <table data-testid="privacy-collection-table">
                <thead>
                    <tr>
                        <th>Category</th>
                        <th>Disclosure</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <th>Inquiry information</th>
                        <td>
                            Full name, mailing address, phone number, email
                            address, insurance topic, and optional notes
                            submitted through the website.
                        </td>
                    </tr>
                    <tr>
                        <th>Communications and consent records</th>
                        <td>
                            Submission date and time, consent-language
                            version, communication history, and related
                            metadata.
                        </td>
                    </tr>
                    <tr>
                        <th>Device and website information</th>
                        <td>
                            IP address, browser user-agent, device
                            information, cookies, analytics data, website
                            security logs, and session diagnostics.
                        </td>
                    </tr>
                    <tr>
                        <th>Uploaded documents</th>
                        <td>
                            Documents submitted through Dropbox or another
                            secure upload provider, including applications,
                            policy records, declarations pages, business
                            records, property information, vehicle schedules,
                            payroll or benefits information, or other
                            user-provided documents.
                        </td>
                    </tr>
                </tbody>
            </table>
            <p data-testid="privacy-sensitive-warning">
                <strong className="text-brand-ivory">
                    Sensitive information warning.
                </strong>{" "}
                Users should not submit Social Security numbers, payment
                information, bank information, medical records, claim
                documents, identity documents, passwords, or other highly
                sensitive information unless specifically instructed through
                an approved secure channel.
            </p>

            <h2>2. How We Use Information</h2>
            <p>
                We use personal information to respond to inquiries, evaluate
                insurance needs and eligibility, match users with licensed
                producers, prepare submissions to insurers or insurance
                intermediaries when appropriate, communicate with carriers or
                other insurance-market participants, send quotes, documents,
                or information, manage customer relationships, administer
                document intake, maintain consent and communication records,
                comply with insurance, tax, legal, and regulatory obligations,
                prevent fraud and misuse, protect website security, and
                operate and improve the Site.
            </p>

            <h2>3. How We Share Information</h2>
            <p>
                We may share personal information and documents with categories
                of recipients reasonably necessary to respond to an inquiry,
                evaluate coverage options, prepare submissions, service
                requests, or comply with law. These recipients may include
                licensed insurance producers, authorized insurers, carrier
                representatives, managing general agents, managing general
                underwriters, wholesalers, surplus-lines brokers, third-party
                administrators, benefits administrators, claims or
                policy-service providers, customer relationship management
                providers, email and communications vendors, website hosting
                and security providers, analytics providers, document-storage
                and secure-upload providers, compliance vendors, professional
                advisors, regulators, law-enforcement authorities, and other
                service providers acting on our behalf.
            </p>
            <p>
                We do not sell personal information for money. If any activity
                constitutes a &ldquo;sale,&rdquo; &ldquo;sharing,&rdquo; or
                targeted advertising disclosure under an applicable state
                privacy law, we will provide any required notice and opt-out
                rights.
            </p>

            <h2>4. Marketing Communications and Opt-Out Rights</h2>
            <p>
                If you ask to receive information from us, submit an inquiry,
                or otherwise communicate with us, we may send follow-up
                communications related to your inquiry. Where permitted by
                law, we may also send marketing communications about insurance
                products or services that may be of interest. You may opt out
                of marketing emails at any time by using the unsubscribe
                instructions in the email or by contacting{" "}
                <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>.
                Opting out of marketing communications will not prevent us
                from sending transactional, servicing, legal, compliance, or
                policy-related communications.
            </p>
            <p>
                We do not currently use SMS/text messaging for marketing
                unless separately disclosed and consented to. If we later use
                SMS, autodialed calls, prerecorded calls, or similar
                communications requiring separate consent, we will obtain any
                consent required by applicable law.
            </p>

            <h2>5. Document Uploads and Dropbox</h2>
            <p>
                Our secure document upload feature may be provided through
                Dropbox or another third-party document-storage provider.
                Files submitted through the upload link are delivered to an{" "}
                {COMPANY.shortName} intake folder and are not intended to be
                publicly accessible or viewable by other users. Dropbox and
                other providers may process information under their own terms
                and privacy notices. Please do not upload Social Security
                numbers, payment information, medical records, claim
                documents, identity documents, passwords, or other highly
                sensitive information unless we specifically instruct you to
                do so through an approved secure channel. We may review,
                retain, share, or delete uploaded documents as reasonably
                necessary to respond to your request, evaluate coverage
                options, prepare submissions, comply with law, or maintain our
                records.
            </p>

            <h2>6. Data Retention</h2>
            <table data-testid="privacy-retention-table">
                <thead>
                    <tr>
                        <th>Data Category</th>
                        <th>Retention Description</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <th>Website inquiry submissions</th>
                        <td>
                            Retained while responding to the inquiry and
                            thereafter as needed for agency records,
                            compliance, dispute resolution, and legal
                            obligations.
                        </td>
                    </tr>
                    <tr>
                        <th>Consent logs and form metadata</th>
                        <td>
                            Retained as needed to demonstrate consent,
                            communication history, and legal compliance.
                        </td>
                    </tr>
                    <tr>
                        <th>Quote, application, and servicing records</th>
                        <td>
                            Retained in accordance with applicable insurance,
                            producer, carrier, tax, and recordkeeping
                            requirements.
                        </td>
                    </tr>
                    <tr>
                        <th>Uploaded documents</th>
                        <td>
                            Retained only as long as reasonably necessary for
                            intake, quote, submission, service, compliance,
                            or legal purposes, then deleted, archived, or
                            anonymized according to agency procedures.
                        </td>
                    </tr>
                    <tr>
                        <th>Analytics and security logs</th>
                        <td>
                            Retained for website security, fraud prevention,
                            diagnostics, and performance monitoring for a
                            limited period consistent with provider settings
                            and business needs.
                        </td>
                    </tr>
                    <tr>
                        <th>Marketing preference records</th>
                        <td>
                            Retained as necessary to honor opt-out requests
                            and document compliance.
                        </td>
                    </tr>
                </tbody>
            </table>
            <p>
                Actual retention periods may vary depending on the type of
                record, applicable insurance laws, carrier requirements, tax
                obligations, legal obligations, dispute-resolution needs, and
                operational requirements. When information is no longer
                reasonably needed, we may delete, anonymize, archive, or
                otherwise restrict it according to our procedures and
                applicable law.
            </p>

            <h2>7. State Privacy Rights</h2>
            <p>
                This section describes privacy rights that may be available
                to residents of <strong>California</strong>,{" "}
                <strong>Colorado</strong>, <strong>Connecticut</strong>,{" "}
                <strong>Virginia</strong>, <strong>Utah</strong>,{" "}
                <strong>Texas</strong>, and other applicable states if legal
                thresholds are met.
            </p>
            <p>
                Depending on your state of residence and whether a particular
                law applies to us, you may have rights to request access to
                personal information, correction of inaccurate information,
                deletion of personal information, portability, opt-out of
                certain processing activities, opt-out of sale or sharing,
                opt-out of targeted advertising, limit certain uses of
                sensitive personal information, appeal a denied request, and
                be free from unlawful discrimination for exercising privacy
                rights. These rights may vary by state and may be subject to
                verification, exceptions, and limitations.
            </p>

            <h3>How to Submit a Privacy Request</h3>
            <table data-testid="privacy-request-table">
                <thead>
                    <tr>
                        <th>Request Method</th>
                        <th>Details</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <th>Email</th>
                        <td>
                            <a href={`mailto:${COMPANY.email}`}>
                                {COMPANY.email}
                            </a>
                        </td>
                    </tr>
                    <tr>
                        <th>Phone</th>
                        <td>
                            <a href={`tel:${COMPANY.phoneTel}`}>
                                {COMPANY.phone}
                            </a>
                        </td>
                    </tr>
                    <tr>
                        <th>Mail</th>
                        <td>{COMPANY.address}</td>
                    </tr>
                </tbody>
            </table>

            <h3>Identity Verification and Authorized Agents</h3>
            <p>
                We may verify your identity before completing a privacy
                request. If you use an authorized agent, we may require proof
                of authorization and may ask you to verify your identity
                directly with us unless prohibited by law. If we deny a
                request and applicable law provides an appeal right, we will
                explain how to appeal in our response.
            </p>

            <h3>Non-Discrimination</h3>
            <p>
                We will not unlawfully discriminate against you for exercising
                privacy rights.
            </p>

            <h3>Browser-Based Opt-Out Signals</h3>
            <p>
                If applicable law requires recognition of browser-based
                opt-out preference signals, we will process Global Privacy
                Control or similar signals in accordance with applicable law
                and our technical capabilities.
            </p>

            <h2>8. Children&apos;s Privacy</h2>
            <p>
                The Site is intended for adults and is not directed to
                children under 13. We do not knowingly collect personal
                information from children under 13. If a parent or guardian
                believes a child submitted personal information through the
                Site, please contact us so we can review and delete the
                information where appropriate.
            </p>

            <h2>9. Cookies, Analytics, and Third-Party Services</h2>
            <p>
                We use the following providers. Each performs a specific
                function and may set cookies or process limited data on our
                behalf:
            </p>
            <ul>
                <li>
                    <strong>Cloudflare</strong> — DNS, content delivery, TLS
                    termination, bot mitigation, and basic web-security
                    functions. Cloudflare may set cookies to identify trusted
                    traffic and prevent abuse.
                </li>
                <li>
                    <strong>PostHog</strong> — product analytics and session
                    diagnostics. We mask form-input contents and other
                    sensitive fields so that text you type into our inquiry
                    form is not captured by PostHog session replay.
                </li>
                <li>
                    <strong>Dropbox</strong> — secure document-upload
                    provider used for the &ldquo;Submit Documents
                    Securely&rdquo; feature, governed by Dropbox&apos;s own
                    terms and privacy notice.
                </li>
                <li>
                    <strong>Hosting / email infrastructure</strong> — used to
                    operate this website and deliver inquiry emails to our
                    licensed producers.
                </li>
            </ul>
            <p>
                You can disable non-essential cookies in your browser
                settings. You can also opt out of PostHog tracking by enabling
                your browser&apos;s &ldquo;Do Not Track&rdquo; or
                &ldquo;Global Privacy Control&rdquo; signal, or by emailing
                the request to{" "}
                <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>.
                Disabling essential cookies (security, fraud prevention) may
                make parts of the site unusable.
            </p>

            <h2>10. Security</h2>
            <p>
                We use commercially reasonable administrative, technical, and
                physical safeguards designed to protect information submitted
                through the Site. However, no method of internet
                transmission, email transmission, file upload, hosting, or
                electronic storage is completely secure. Do not send highly
                sensitive information through unencrypted email or the
                general inquiry form. If sensitive information is needed,
                contact us for an appropriate channel.
            </p>

            <h2>11. Electronic Communications Consent (E-SIGN / UETA)</h2>
            <p>
                When you submit our inquiry form, contact us by email, or
                otherwise initiate communication through this Site, you
                consent to receive communications, disclosures, notices,
                agreements, and other records from {COMPANY.fullLegalName}{" "}
                and its licensed producers in electronic form (including by
                email and through this website) instead of on paper, where
                permitted by law.
            </p>
            <p>
                <strong>Hardware and software requirements.</strong> To
                access and retain electronic records you need: (a) a current
                internet browser; (b) a working email address; (c) sufficient
                electronic storage capacity or a printer.
            </p>
            <p>
                <strong>
                    Withdrawing your consent or requesting paper copies.
                </strong>{" "}
                You may withdraw your consent to receive electronic
                communications, or request a paper copy of any record
                provided electronically, at any time by emailing{" "}
                <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a> or
                calling{" "}
                <a href={`tel:${COMPANY.phoneTel}`}>{COMPANY.phone}</a>.
                Withdrawing consent will not affect the legal validity of
                records previously delivered electronically. We may charge a
                reasonable fee for paper copies if permitted by law.
            </p>
            <p>
                <strong>Updating your contact information.</strong> You are
                responsible for keeping your email and other contact
                information current with us.
            </p>

            <h2>12. Changes to This Notice</h2>
            <p>
                We may update this Privacy Notice from time to time. The
                &ldquo;Last Updated&rdquo; date at the top of this page shows
                when it was last revised. If changes are material, we may
                provide additional notice where required by law, such as by
                posting a prominent notice on the Site or contacting you by
                email.
            </p>

            <h2>13. Related Documents</h2>
            <p>
                This Privacy Notice should be read together with our{" "}
                <Link to="/terms">Terms of Service</Link>, which govern your
                access to and use of this Site, and our{" "}
                <Link to="/licensing">Licensing &amp; Disclosures</Link>,
                which describe where we are licensed to do business.
            </p>

            <h2>14. Contact Us</h2>
            <p>Questions about this Privacy Notice or your information?</p>
            <p>
                {COMPANY.fullLegalName}
                <br />
                {COMPANY.address}
                <br />
                Phone:{" "}
                <a href={`tel:${COMPANY.phoneTel}`}>{COMPANY.phone}</a>
                <br />
                Email:{" "}
                <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>
                <br />
                Website:{" "}
                <a href={COMPANY.website} rel="noopener noreferrer">
                    {COMPANY.website}
                </a>
            </p>

            <p>
                <Link to="/contact">Visit our Contact page →</Link>
            </p>
        </PageShell>
    );
}
